import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { Container, Button, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { RETURNS_LOG_PATHS } from "Routes/paths";
import { useReturnsAccountability } from "modules/Pharmacy/ReturnsLog/api/useReturnsAccountability";
import Reason, { ReasonSubmitPayload } from "common/utilities/Reason/Reason";
import { showToastMessage } from "common/service/CommonService";
import { validations } from "validations/FormValidations";
import {
  IP_UNIT_OPTIONS,
  IP_PHYSICAL_CONDITION_OPTIONS,
  IP_RETURN_SOURCE_TYPE_OPTIONS,
  IP_RETURN_REASON_OPTIONS,
  loadCodelistsDetailId,
} from "api/modules/common.option";
import SectionCard from "Components/Common/SectionCard";
import FullPageLoader from "Components/Common/FullPageLoader";
import { BreadcrumbContext } from "Layouts/Header";
import { dispensingService } from "modules/Pharmacy/Dispensing/api/dispensing.api";
import { useGlobalStudy } from "context/StudyContext";
import GlobalStudyMismatchWarning from "../../../../Components/Common/GlobalStudyMismatchWarning";
import { queryClient } from "api/queryClient";

/* =========================
   TYPES
========================= */

type ReturnsLogFormValues = {
  returnid: number;
  returnno: string;
  studyCode: string;
  returnDate: string;
  returnSourceType: string;
  subjectId: string;
  dispensingId: string;
  productname: string;
  batchLotNumber: string;
  ipcategory: string;
  unitType: string;
  qtyReturned: number | string | undefined;
  dispensedQty: number | undefined;
  condition: string;
  reason: string;
  comments: string;
};

/* =========================
   SECTION CARD
========================= */



const AddReturnsLog: React.FC = () => {
  const navigate = useNavigate();
  const breadcrumbContext = useContext(BreadcrumbContext);
  const [searchParams] = useSearchParams();
  const { id: paramId } = useParams();
  const editId = paramId || searchParams.get("id");

  document.title = `${editId ? "Edit" : "Add"} Returns Log | ZENTIXS ORBIT`;

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Returns Log", link: RETURNS_LOG_PATHS.LIST },
      { text: editId ? "Edit Return Log" : "Add Return Log" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [editId]);

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [formData, setFormData] = useState<ReturnsLogFormValues | null>(null);
  const [codelistsReady, setCodelistsReady] = useState(false);
  const [inventoryList, setInventoryList] = useState<any[]>([]);

  const isPrefillingRef = useRef(false);
  const prefilledStudyRef = useRef<string>("");

  const { InsertMutation, UpdateMutation, SelectQuery } = useReturnsAccountability({
    returnid: editId ? Number(editId) : null,
  });

  const { DropdownQuery: studySelectAllQuery } = useReturnsAccountability({
    getStudyDropDown: true,
  });

  const studyOptions = useMemo(
    () =>
      (studySelectAllQuery.data as any)?.data?.map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
        studycode: study.studycode,
      })) ?? [],
    [studySelectAllQuery.data]
  );

  const productOptions = useMemo(
    () =>
      inventoryList.map((item: any) => ({
        label: `${item.productname} `,
        value: String(item.dispensingid),
      })),
    [inventoryList]
  );

  const clearInventoryFields = () => {
    setValue("productname", "");
    setValue("ipcategory", "");
    setValue("batchLotNumber", "");
    setValue("unitType", "");
  };

  useEffect(() => {
    const load = async () => {
      const result = await loadCodelistsDetailId([
        "IP_UNIT_OPTIONS",
        "IP_PHYSICAL_CONDITION_OPTIONS",
        "IP_RETURN_SOURCE_TYPE_OPTIONS",
        "IP_RETURN_REASON_OPTIONS",
      ]);
      const allLoaded = Object.values(result).every((flag) => flag);
      if (!allLoaded) {
        showToastMessage("error", "Some dropdowns failed to load");
      }
      setCodelistsReady(true);
    };
    load();
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ReturnsLogFormValues>({
    defaultValues: {
      returnid: 0,
      returnno: "",
      studyCode: "",
      returnDate: "",
      returnSourceType: "",
      subjectId: "",
      dispensingId: "",
      productname: "",
      batchLotNumber: "",
      ipcategory: "",
      unitType: "",
      qtyReturned: undefined,
      dispensedQty: undefined,
      condition: "",
      reason: "",
      comments: "",
    },
    mode: "onBlur",
  });

  const { selectedStudy: globalStudy } = useGlobalStudy();

  useEffect(() => {
    if (editId) return;
    if (globalStudy?.studyid && studyOptions.length > 0) {
      const matched = studyOptions.find(
        (s: any) => String(s.value) === String(globalStudy.studyid)
      );
      if (matched) {
        setValue("studyCode", matched.value, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    } else {
      setValue("studyCode", "", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [globalStudy?.studyid, studyOptions, editId, setValue]);

  const selectedStudyId = watch("studyCode");

  useEffect(() => {
    if (!codelistsReady) return;
    if (studyOptions.length === 0) return;

    const record = SelectQuery.data?.data?.[0];
    if (record) {
      isPrefillingRef.current = true;
      prefilledStudyRef.current = record.studyid ? String(record.studyid) : "";

      const qty = record.quantityreturned ?? undefined;

      reset({
        returnid: record.returnid ?? 0,
        returnno: record.returnno ?? "",
        studyCode: record.studyid ?? "",
        returnDate: record.returndate ? record.returndate.split("T")[0] : "",
        returnSourceType: record.returnsourcetype ?? "",
        subjectId: record.subjectno || "",
        dispensingId: "",
        productname: record.productname || "",
        batchLotNumber: record.batchlotno || "",
        ipcategory: record.ipcategory || "",
        unitType: record.unittype ? String(record.unittype) : record.unittype ? String(record.unittype) : "",
        qtyReturned: qty,
        condition: record.conditionreturned ?? "",
        reason: record.reasonforreturn ?? "",
        comments: record.comments || "",
      });

      isPrefillingRef.current = false;
    }
  }, [SelectQuery.data, codelistsReady, studyOptions, reset]);

  const selectedStudy = watch("studyCode");
  const selectedProduct = watch("productname");
  const qtyReturned = watch("qtyReturned");
  const subjectId = watch("subjectId");
  const studyid = watch("studyCode");
  const ipcategory = watch("ipcategory");

  const { subjectDropDown } = useReturnsAccountability({
    studyid: Number(studyid),
    ipcategory: ipcategory
  });

  const subjectOptions = useMemo(
    () =>
      (subjectDropDown.data as any)?.data
        ?.filter((item: any) => item.subjectno)
        ?.map((item: any) => ({
          label: String(item.subjectno),
          value: String(item.subjectno),
        }))

        ?.filter((item: any, index: number, self: any[]) =>
          index === self.findIndex((t) => t.value === item.value)
        ) ?? [],
    [subjectDropDown.data]
  );

  useEffect(() => {
    if (!subjectId) {
      setValue("dispensedQty", undefined);
      return;
    }

    const currentSubjectData = (subjectDropDown.data as any)?.data?.find(
      (item: any) => String(item.subjectno) === String(subjectId)
    );

    if (currentSubjectData) {
      setValue("dispensedQty", currentSubjectData.currentstock);
    } else {
      setValue("dispensedQty", undefined);
    }
  }, [subjectId, subjectDropDown.data, setValue]);

  useEffect(() => {

    if (!selectedStudy) {
      setInventoryList([]);
      clearInventoryFields();
      return;
    }

    if (isPrefillingRef.current) return;

    clearInventoryFields();
    setInventoryList([]);

    dispensingService
      .select({ studyid: selectedStudy })
      .then((res) => {
        const list = res?.data || [];

        setInventoryList(list);

        if (list.length === 0) {
          clearInventoryFields();
          return;
        }
        const first = list[0];
        setValue("productname", String(first.dispensingid));
      })
      .catch((err) => {
        console.error("Inventory fetch failed:", err);
        clearInventoryFields();
      });

  }, [selectedStudy]);

  useEffect(() => {
    if (!selectedProduct) {
      setValue("ipcategory", "");
      setValue("batchLotNumber", "");
      setValue("unitType", "");
      return;
    }

    const product = inventoryList.find(
      (item) => String(item.dispensingid) === String(selectedProduct)
    );

    if (!product) return;

    setValue("batchLotNumber", product.batchlotno || "");
    setValue("ipcategory", product.ipcategory || "");
    setValue("unitType", product.unittype || "");
  }, [selectedProduct, inventoryList, setValue]);

  useEffect(() => {
    if (qtyReturned === undefined || qtyReturned === "") return;

    const returned = Number(qtyReturned);
    if (!isNaN(returned) && typeof qtyReturned === "string") {
      setValue("qtyReturned", returned);
    }
  }, [qtyReturned, setValue]);

  const section1: FormFieldConfig[] = [
    {
      name: "studyCode",
      label: "Study Code",
      type: "select",
      options: studyOptions,
      required: true,
      disabled: true,
      placeholder: "Select study code",
      rules: validations.returnsLog.studyCode,
    },
    {
      name: "returnDate",
      label: "Return Date",
      type: "date",
      placeholder: "Enter return date",
      required: true,
      rules: validations.returnsLog.returnDate,
    },
    {
      name: "returnSourceType",
      label: "Return Source Type",
      type: "select",
      options: IP_RETURN_SOURCE_TYPE_OPTIONS,
      required: true,
      placeholder: "Select return source type...",
      rules: validations.returnsLog.returnSourceType,
    },
  ];

  const section2Bottom: FormFieldConfig[] = [
    {
      name: "productname",
      label: "Product Name",
      type: "select",
      placeholder:
        productOptions.length === 0
          ? "No Investigational Product Available"
          : "Select product name",
      disabled: productOptions.length === 0 || !!editId,
      required: true,
      options: productOptions,
      rules: validations.returnsLog.productname,
    },
    {
      name: "ipcategory",
      label: "IP Category",
      type: "text",
      placeholder: "IP Category",
      required: true,
      disabled: true,
      rules: validations.returnsLog.ipcategory,
    },
    {
      name: "batchLotNumber",
      label: "Batch / Lot Number",
      type: "text",
      placeholder: "Enter batch / lot number",
      required: true,
      disabled: true,
      rules: validations.returnsLog.batchLotNumber,
    },
    {
      name: "unitType",
      label: "Unit Type",
      type: "select",
      options: IP_UNIT_OPTIONS,
      disabled: true,
      required: true,
      placeholder: "Select Unit...",
      rules: validations.returnsLog.unitType,
    },
  ];

  const section3: FormFieldConfig[] = [
    {
      name: "subjectId",
      label: "Subject Number",
      type: "select",
      options: subjectOptions,
      required: true,
      placeholder: !selectedStudyId ? "Select a Study First..." : "Select Subject Number...",
      disabled: !selectedStudyId || subjectDropDown.isLoading || !!editId,
      rules: validations.returnsLog.subjectId
    },
    {
      name: "dispensedQty",
      label: "Dispensed Quantity",
      type: "number",
      placeholder: "Dispensed Quantity",
      disabled: true,
    },
    {
      name: "qtyReturned",
      label: "Quantity Returned",
      type: "number",
      placeholder: "Quantity Returned",
      required: true,
      disabled: !subjectId,
      rules: {
        ...validations.returnsLog.qtyReturned,
        validate: (value: number) => {
          if (value <= 0) {
            return "Quantity returned must be greater than 0";
          }
          const dispensed = watch("dispensedQty");
          if (dispensed !== undefined && value > dispensed) {
            return `Returned quantity (${value}) cannot exceed dispensed quantity (${dispensed})`;
          }
          return true;
        },
      },
    },
    {
      name: "condition",
      label: "Condition of Returned IP",
      type: "select",
      options: IP_PHYSICAL_CONDITION_OPTIONS,
      required: true,
      placeholder: "Select Condition...",
      rules: validations.returnsLog.condition,
    },
    {
      name: "reason",
      label: "Reason for Return",
      type: "select",
      options: IP_RETURN_REASON_OPTIONS,
      required: true,
      placeholder: "Select Reason...",
      rules: validations.returnsLog.reason,
    },
    {
      name: "comments",
      label: "Comments / Notes",
      type: "textarea",
      placeholder: "Enter comments ",
      rules: validations.returnsLog.comments,
    },
  ];

  const Payload = (data: ReturnsLogFormValues) => {
    const selectedProduct = inventoryList.find(
      (item) => String(item.dispensingid) === String(data.productname)
    );

    return {
      studyid: data.studyCode,
      studycode: studyOptions.find((s: any) => s.value === data.studyCode)?.label ?? "",
      returndate: data.returnDate,
      returnsourcetype: Number(data.returnSourceType),
      subjectno: data.subjectId,
      productname: selectedProduct?.productname || "",
      batchlotno: data.batchLotNumber,
      ipcategory: data.ipcategory,
      unittype: Number(data.unitType),
      quantityreturned: Number(data.qtyReturned),
      conditionreturned: Number(data.condition),
      reasonforreturn: Number(data.reason),
      comments: data.comments || null,
    };
  };

  const onSubmit = (data: ReturnsLogFormValues) => {
    if (editId) {
      setFormData(data);
      setIsUpdateModalOpen(true);
    } else {
      handleInsert(data);
    }
  };

  const handleInsert = (data: ReturnsLogFormValues) => {
    InsertMutation.mutate(Payload(data), {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        showToastMessage("success", res?.message || "Return log saved successfully");
        navigate(RETURNS_LOG_PATHS.LIST);
      },
      onError: (err: any) => {
        showToastMessage("error", err || "Something Went Wrong !! ");
      },
    });
  };

  const handleUpdate = (reasonPayload: ReasonSubmitPayload) => {
    if (!formData || !editId) return;

    const payload = {
      ...Payload(formData),
      returnid: Number(editId),
      updatereason: reasonPayload.reason,
    };

    UpdateMutation.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message || "Return log updated successfully");
        setIsUpdateModalOpen(false);
        navigate(RETURNS_LOG_PATHS.LIST);
      },
      onError: (err: any) => {
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.message ||
          "Something went wrong";
        showToastMessage("error", message);
      },
    });
  };

  if (editId && (SelectQuery.isLoading || SelectQuery.isFetching || !codelistsReady)) {
    return (
      <Container fluid className={`px-0 ${formStyles.pageContainer}`}>
        <FullPageLoader />
      </Container>
    );
  }

  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      <Container fluid>
        <form onSubmit={handleSubmit(onSubmit)}>

          {/* Study Mismatch Warning */}
          <GlobalStudyMismatchWarning
            globalStudy={globalStudy}
            studyOptions={studyOptions}
            isLoaded={!studySelectAllQuery.isLoading}
            isreturned={true}
          />

          {/* 1 – General Information */}
          <SectionCard
            index={1}
            icon="ri-hospital-line"
            title="General Information"
            subtitle="Enter return source and protocol details"
          >
            <CommonForm fields={section1} control={control} errors={errors} columns={3} />
          </SectionCard>

          {/* 2 – Product Information */}
          <SectionCard
            index={2}
            icon="ri-capsule-line"
            title="Investigational Product Details"
            subtitle="Identify the product being returned"
          >
            <CommonForm fields={section2Bottom} control={control} errors={errors} columns={3} />
          </SectionCard>

          {/* 3 – Return Details */}
          <SectionCard
            index={3}
            icon="ri-survey-line"
            title="Return Details"
            subtitle="Log quantities and condition"
          >
            <CommonForm fields={section3} control={control} errors={errors} columns={3} />
          </SectionCard>

          {/* Sticky Footer */}
          <div className={formStyles.stickyFooter}>
            <div className={formStyles.footerActions}>
              <Button
                type="button"
                color="dark"
                outline
                onClick={() => navigate(RETURNS_LOG_PATHS.LIST)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                outline
                disabled={InsertMutation.isPending || UpdateMutation.isPending}
              >
                {InsertMutation.isPending && <Spinner size="sm" className="me-2" />}
                {editId ? "Update" : "Save"}
              </Button>
            </div>
          </div>

        </form>

        <Reason
          isOpen={isUpdateModalOpen}
          toggle={() => setIsUpdateModalOpen(false)}
          action="Update Record"
          id={Number(editId)}
          click={handleUpdate}
          isLoading={UpdateMutation.isPending}
        />

      </Container>
    </div>
  );
};

export default AddReturnsLog;